import React from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, Package, ShieldCheck, Truck, MessageCircle, CheckCircle2, Plane } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { popularCountries, medicineTypes, contact } from "../mock";

export default function CountryDetail() {
  const { slug } = useParams();
  const country = popularCountries.find((c) => c.slug === slug);

  if (!country) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Destination not found</h1>
        <Link to="/countries" className="text-teal-600 font-semibold">← Back to Countries</Link>
      </div>
    );
  }

  const rows = [
    { type: "Allopathic Medicines", weight: "0.5 kg", price: country.price },
    { type: "Ayurvedic & Herbal", weight: "0.5 kg", price: country.price },
    { type: "Homeopathic Medicines", weight: "0.5 kg", price: country.price },
    { type: "Life Saving / Chronic", weight: "0.5 kg", price: country.price },
  ];

  const features = [
    { icon: Truck, title: "Free Doorstep Pickup", desc: "We collect medicines from your home anywhere in India for free." },
    { icon: Package, title: "Compliance Packing", desc: "Secure, regulatory packing with cold-pack support if required." },
    { icon: ShieldCheck, title: "Customs Clearance", desc: `Full documentation support for smooth clearance into ${country.name}.` },
    { icon: Plane, title: "Priority Dispatch", desc: "Shipped via DHL/FedEx networks with live tracking." },
  ];

  return (
    <>
      <Seo
        title={`India to ${country.name} Medicine Courier Charges | ShipMeds`}
        description={`Send medicines from India to ${country.name} with ShipMeds. Free doorstep pickup, guaranteed delivery in ${country.days} days, customs help. Charges from ₹${country.price.toLocaleString()} for 0.5 kg.`}
        path={`/country/${country.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": `India to ${country.name} Medicine Courier`,
          "provider": { "@type": "Organization", "name": "ShipMeds" },
          "areaServed": country.name,
          "offers": { "@type": "Offer", "price": String(country.price), "priceCurrency": "INR" },
          "description": `Doorstep pickup in India and international delivery of medicines to ${country.name} in ${country.days} working days with customs documentation support.`,
        }}
      />
      <PageHero title={`India to ${country.name} Medicine Courier`} subtitle={`Send medicines from India to ${country.name} with guaranteed doorstep delivery in ${country.days} working days.`} crumb={country.name} />

      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-4 mb-6">
              <img src={`https://flagcdn.com/w160/${country.code}.png`} alt={country.name} className="w-16 h-11 object-cover rounded-md shadow-sm" />
              <div>
                <h2 className="text-2xl font-extrabold text-slate-900">Medicine Courier Charges to {country.name}</h2>
                <p className="text-slate-500 text-sm">Capital: {country.capital} · Delivery: {country.days} working days</p>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed mb-6">ShipMeds offers the cheapest and most reliable medicine courier service from India to {country.name}. Whether you need us to pick up your own medicines or buy them on your behalf, we handle packing, documentation and customs clearance end to end.</p>

            <div className="overflow-hidden rounded-2xl border border-slate-100 card-shadow">
              <table className="w-full text-left">
                <thead className="bg-teal-600 text-white text-sm">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Medicine Type</th>
                    <th className="px-5 py-3 font-semibold">Weight</th>
                    <th className="px-5 py-3 font-semibold text-right">Courier Charges</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {rows.map((r, i) => (
                    <tr key={r.type} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                      <td className="px-5 py-3.5 font-medium text-slate-700">{r.type}</td>
                      <td className="px-5 py-3.5 text-slate-500">{r.weight}</td>
                      <td className="px-5 py-3.5 text-right font-bold text-teal-700">₹{r.price.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-slate-400">*Indicative charges for 0.5 kg shipments. Final rate confirmed by our team on booking.</p>

            <div className="grid sm:grid-cols-2 gap-5 mt-10">
              {features.map((f) => (
                <div key={f.title} className="flex gap-4 bg-slate-50 rounded-2xl p-5 border border-slate-100">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-500 to-blue-600 text-white flex items-center justify-center shrink-0"><f.icon className="w-5 h-5" /></div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">{f.title}</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 h-fit">
            <div className="bg-gradient-to-br from-teal-50 to-white border border-teal-100 rounded-2xl p-6">
              <div className="text-center mb-5">
                <p className="text-slate-500 text-sm">Starting from</p>
                <div className="text-4xl font-extrabold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">₹{country.price.toLocaleString()}</div>
                <p className="text-slate-500 text-sm">for 0.5 kg to {country.name}</p>
              </div>
              <ul className="space-y-2.5 mb-6">
                {[`Delivery in ${country.days} working days`, "Free doorstep pickup in India", "Documentation & customs help", "24x7 support & live tracking"].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-teal-500 shrink-0 mt-0.5" />{t}</li>
                ))}
              </ul>
              <a href={contact.whatsapp} target="_blank" rel="noreferrer" className="w-full inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-5 py-3 rounded-lg transition-colors mb-3"><MessageCircle className="w-5 h-5" /> Book on WhatsApp</a>
              <Link to={`/customs-clearance/${country.slug}`} className="w-full inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold px-5 py-3 rounded-lg transition-colors"><ShieldCheck className="w-4 h-4" /> Customs Clearance in {country.name}</Link>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
